import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/context/NotificationContext";
import { toast } from "sonner";
import { Bell, WhatsappLogo, EnvelopeSimple, CheckCircle, ShieldCheck, PencilSimple } from "@phosphor-icons/react";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refresh } = useNotifications();
  const [items, setItems] = useState([]);
  const [grantBusy, setGrantBusy] = useState(null);
  const [resolvedIds, setResolvedIds] = useState(new Set());
  const load = () => api.get("/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); refresh(); };
  const openN = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    refresh();
    if (n.meta?.type === "order_missing_fields" && n.meta?.order_id) {
      navigate(`/orders/${n.meta.order_id}?edit=1${n.meta.focus_field ? `&focus=${n.meta.focus_field}` : ""}`);
    } else if (n.task_id) navigate(`/tasks/${n.task_id}`); else load();
  };

  // Quick-action: an admin can grant a requested file straight from the notification, without
  // navigating to the Access Requests inbox first.
  const quickGrant = async (n, e) => {
    e.stopPropagation();
    const reqId = n.meta?.access_request_id;
    if (!reqId) return;
    setGrantBusy(n.id);
    try {
      await api.post(`/access-requests/${reqId}/grant`);
      toast.success("Access granted");
      await api.post(`/notifications/${n.id}/read`);
      setResolvedIds((p) => new Set(p).add(reqId));
      refresh();
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not grant access";
      toast.error(msg);
      if (msg.toLowerCase().includes("already")) setResolvedIds((p) => new Set(p).add(reqId));
    } finally { setGrantBusy(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[900px] animate-fade-in">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Alerts</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 flex items-center gap-2"><Bell size={26} className="text-emerald-600" /> Notifications</h1>
        </div>
        <Button variant="outline" onClick={markAll} className="border-zinc-800 bg-zinc-900 rounded-sm text-xs gap-1" data-testid="mark-all-read"><CheckCircle size={14} /> Mark all read</Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center">No notifications.</div>}
        {items.map((n) => {
          const isAccessRequest = n.meta?.type === "access_request" && n.meta?.access_request_id;
          const isResolved = isAccessRequest && resolvedIds.has(n.meta.access_request_id);
          const isMissingFields = n.meta?.type === "order_missing_fields" && n.meta?.order_id;
          return (
            <div key={n.id} data-testid={`notif-${n.id}`}
              className={`w-full flex items-start gap-3 border p-4 transition-colors ${n.read ? "border-zinc-800 bg-zinc-900/20" : "border-emerald-600/30 bg-emerald-600/5"}`}>
              <button onClick={() => openN(n)} className="flex items-start gap-3 flex-1 min-w-0 text-left">
                {!n.read && <span className="w-2 h-2 bg-emerald-600 rounded-full mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 font-medium">{n.title}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{n.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] font-mono text-zinc-600">{fmtDate(n.at)}</span>
                    {(n.channels || []).includes("whatsapp") && <WhatsappLogo size={13} className="text-emerald-500" />}
                    {(n.channels || []).includes("email") && <EnvelopeSimple size={13} className="text-blue-400" />}
                  </div>
                </div>
              </button>
              {isAccessRequest && !isResolved && (
                <Button
                  size="sm" disabled={grantBusy === n.id} onClick={(e) => quickGrant(n, e)}
                  className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid={`notif-quick-grant-${n.id}`}
                >
                  <ShieldCheck size={14} /> Grant Access
                </Button>
              )}
              {isMissingFields && (
                <Button
                  size="sm" onClick={(e) => { e.stopPropagation(); openN(n); }}
                  className="shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1" data-testid={`notif-fill-in-${n.id}`}
                >
                  <PencilSimple size={14} /> Fill it in now
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
